import React, { useState, useMemo, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ColorInput,
  Paper,
  Stack,
  Text,
  Group,
  Title,
  Button,
} from "@mantine/core";
import { Palette, Save } from "lucide-react";
import HomepageThemeOne from "../HomepageThemes/One";
import HomepageThemeTwo from "../HomepageThemes/Two";
import { HomepageThemeThree } from "../HomepageThemes/Three";
import HomepageThemeFour from "../HomepageThemes/Four";
import HomepageThemeFive from "../HomepageThemes/Five";
import { Loader } from "../../../components/ui/loader";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { useToastNotification } from "../../../hooks/useToast";
import "./ThemePreview.scss";

const ThemePreview: React.FC = () => {
  const { id, themeId } = useParams<{ id: string; themeId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPrimaryColor = searchParams.get("primaryColor") || "#17265a";
  const initialSecondaryColor = searchParams.get("secondaryColor") || "#FFFFFF";
  const [primaryColor, setPrimaryColor] = useState<string>(initialPrimaryColor);
  const [secondaryColor, setSecondaryColor] =
    useState<string>(initialSecondaryColor);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { success, error } = useToastNotification();

  const organizationId = id ? parseInt(id, 10) : null;

  // Ensure Mantine popover appears above everything
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .mantine-Popover-dropdown,
      .mantine-ColorInput-dropdown,
      .mantine-ColorPicker-dropdown,
      [data-mantine-popover],
      [data-mantine-color-input-dropdown] {
        z-index: 10000 !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Map themeId to website_index
  const websiteIndexMap: Record<string, number> = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
  };

  const websiteIndex = themeId ? websiteIndexMap[themeId] || 1 : 1;

  const handlePrimaryColorChange = (color: string) => {
    setPrimaryColor(color);
    setSearchParams({
      primaryColor: color,
      secondaryColor: secondaryColor,
    });
  };

  const handleSecondaryColorChange = (color: string) => {
    setSecondaryColor(color);
    setSearchParams({
      primaryColor: primaryColor,
      secondaryColor: color,
    });
  };

  const handleSaveTheme = async () => {
    if (!organizationId) {
      error("Organization ID is missing");
      return;
    }

    setIsSaving(true);
    try {
      const endpoint = Endpoints.GetSetOrganizationTheme();
      const payload = {
        organization_id: organizationId,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        website_index: websiteIndex,
      };

      const response = await PerformRequest({
        method: endpoint.method as any,
        route: endpoint.route,
        data: payload,
      });

      if (response.status === 200 || response.status === 201) {
        success("Theme saved successfully!");
      } else {
        error("Failed to save theme");
      }
    } catch (err: any) {
      console.error("Error saving theme:", err);
      error(err?.response?.data?.message || "Failed to save theme");
    } finally {
      setIsSaving(false);
    }
  };

  if (!organizationId) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: "1rem",
        }}
      >
        <Loader size="lg" />
        <Text c="dimmed">Loading preview...</Text>
      </div>
    );
  }

  // Render the appropriate theme based on themeId
  const renderTheme = () => {
    switch (themeId) {
      case "one":
        return (
          <HomepageThemeOne
            organizationId={organizationId}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />
        );
      case "two":
        return (
          <HomepageThemeTwo
            organizationId={organizationId}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />
        );
      case "three":
        return (
          <HomepageThemeThree
            organizationId={organizationId}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />
        );
      case "four":
        return (
          <HomepageThemeFour
            organizationId={organizationId}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />
        );
      case "five":
        return (
          <HomepageThemeFive
            organizationId={organizationId}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />
        );
      default:
        return null;
    }
  };

  const theme = renderTheme();
  if (!theme) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <Text c="dimmed">Theme not found</Text>
      </div>
    );
  }

  return (
    <>
      {theme}
      <div className="theme-preview-color-picker">
        <Paper
          p="md"
          radius="md"
          withBorder
          className={`color-picker-panel ${showColorPicker ? "expanded" : ""}`}
        >
          <Group
            gap="sm"
            style={{ cursor: "pointer" }}
            onClick={() => setShowColorPicker(!showColorPicker)}
          >
            <Palette size={20} style={{ color: primaryColor }} />
            <Text size="sm" fw={600}>
              Customize Colors
            </Text>
          </Group>
          {showColorPicker && (
            <Stack gap="md" mt="md">
              <ColorInput
                label="Primary Color"
                value={primaryColor}
                onChange={handlePrimaryColorChange}
                format="hex"
                swatches={[
                  "#17265a",
                  "#2563eb",
                  "#7c3aed",
                  "#dc2626",
                  "#ea580c",
                  "#ca8a04",
                  "#16a34a",
                  "#0891b2",
                  "#0284c7",
                  "#9333ea",
                ]}
                size="sm"
              />
              <ColorInput
                label="Secondary Color"
                value={secondaryColor}
                onChange={handleSecondaryColorChange}
                format="hex"
                swatches={[
                  "#FFFFFF",
                  "#F9FAFB",
                  "#F3F4F6",
                  "#E5E7EB",
                  "#D1D5DB",
                  "#9CA3AF",
                  "#6B7280",
                  "#4B5563",
                  "#374151",
                  "#1F2937",
                ]}
                size="sm"
              />
              <Button
                leftSection={<Save size={16} />}
                onClick={handleSaveTheme}
                loading={isSaving}
                fullWidth
                style={{ backgroundColor: primaryColor }}
                mt="sm"
              >
                Save Theme
              </Button>
            </Stack>
          )}
        </Paper>
      </div>
    </>
  );
};

export default ThemePreview;

